import React, { useId } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { PRES, TYPE } from '../chrome';
import { COPY } from '../presentationCopy';
import { focusVisibleSx } from '../ui/focus';

interface AdminInfoLabelProps {
  label: string;
  htmlFor?: string;
  hint?: string;
  hintId?: string;
  info?: string;
}

const AdminInfoLabel: React.FC<AdminInfoLabelProps> = ({ label, htmlFor, hint, hintId, info }) => {
  const generatedHintId = useId();
  const resolvedHintId = hintId || generatedHintId;
  return (
    <Box sx={{ mb: hint ? 0.75 : 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minHeight: 20 }}>
        <Typography
          component="label"
          htmlFor={htmlFor}
          sx={{ color: PRES.textSecondary, fontFamily: TYPE.ui, ...TYPE.label }}
        >
          {label}
        </Typography>
        {info && (
          <Tooltip title={info} enterDelay={200} describeChild>
            <IconButton
              size="small"
              aria-label={`${COPY.settingsMoreInfo}: ${label}`}
              sx={{ color: PRES.textTertiary, p: 0.25, ...focusVisibleSx }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      {hint && (
        <Typography id={resolvedHintId} sx={{ mt: 0.35, color: PRES.textTertiary, fontFamily: TYPE.ui, ...TYPE.caption }}>
          {hint}
        </Typography>
      )}
    </Box>
  );
};

interface AdminLabeledControlProps {
  label: string;
  hint?: string;
  info?: string;
  children: (ids: { id: string; describedBy?: string }) => React.ReactNode;
}

export const AdminLabeledControl: React.FC<AdminLabeledControlProps> = ({ label, hint, info, children }) => {
  const id = useId();
  const hintId = useId();
  return (
    <Box>
      <AdminInfoLabel
        label={label}
        htmlFor={id}
        hint={hint}
        hintId={hint ? hintId : undefined}
        info={info}
      />
      {children({ id, describedBy: hint ? hintId : undefined })}
    </Box>
  );
};

export default AdminInfoLabel;
